import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';
import { LOOM_ATI } from '@atlassian/universal-create-common';

import { REQUIRED_USER_ITEMS } from '../../../../constants';
import type { CrossAppCreationConfig, MenuSectionV2, SourceProduct } from '../../../../types';
import { sectionsToAppMenuConfig } from '../../MenuContentV2/utils/configConverters';
import { useLoomVideoConfig } from '../../SuggestionItem/primitives/LoomItem/hooks/use-loom-video-config';
import { convertSourceProductToProductType } from '../../SuggestionItem/primitives/LoomItem/LoomVideoItem';

interface UsePrefetchV2Props {
	crossAppCreationConfig: CrossAppCreationConfig;
	product: SourceProduct;
	sections: MenuSectionV2[];
	skip?: boolean;
	subProduct?: string;
}

interface UsePrefetchV2Return {
	shouldFetchLoom: boolean;
	shouldFetchUser: boolean;
	shouldFetchV2Data: boolean;
}

/**
 * Hook to determine prefetch requirements for V2 cross-app creation.
 * This hook only determines what needs to be fetched, it does not perform the actual fetching.
 *
 * @param crossAppCreationConfig - The cross-app creation configuration
 * @param product - The current product
 * @param subProduct - The current sub-product (optional)
 * @param sections - Menu sections for V2
 * @param skip - Skip all prefetch operations and return false for all flags
 * @returns Object with prefetch requirement flags
 */
export function usePrefetchV2({
	crossAppCreationConfig,
	product,
	subProduct,
	sections,
	skip = false,
}: UsePrefetchV2Props): UsePrefetchV2Return {
	const crossAppCreationEnabled = fg('navx_uc_cross_app_creation_v2_enabled');
	const { locale } = useIntl();

	const shouldSkip = skip || !crossAppCreationEnabled || !crossAppCreationConfig.enabled;

	const shouldFetchV2Data =
		!shouldSkip && crossAppCreationConfig.enabled && crossAppCreationConfig.prefetch === true;

	// Convert sections to app menu config for V2 API (memoized to avoid recalculation)
	const appMenuConfig = useMemo(() => sectionsToAppMenuConfig(sections), [sections]);

	// Fetch V2 menu items
	const { data, hasError, fetchUCMenuItems } = useUCMenuItems({
		cloudId: crossAppCreationConfig.enabled ? crossAppCreationConfig.cloudId : '',
		orgId: crossAppCreationConfig.enabled ? crossAppCreationConfig.orgId : '',
		userId: crossAppCreationConfig.enabled ? crossAppCreationConfig.accountId : '',
		product,
		locale,
		subProduct,
		appMenuConfig,
		skipInitialization: !shouldFetchV2Data,
	});

	// Skip prefetching if there's an error
	const shouldSkipDueToError = hasError;

	// Check if Loom is enabled in the fetched data (only recalculate when data changes)
	const hasLoomEnabled = useMemo(() => {
		if (!data || shouldSkipDueToError || !data.sections || !Array.isArray(data.sections)) {
			return false;
		}

		// Check all sections for Loom items
		return data.sections.some(
			(section) =>
				section &&
				section.items &&
				Array.isArray(section.items) &&
				section.items.some((item) => item && item.ati === LOOM_ATI),
		);
	}, [data, shouldSkipDueToError]);

	const shouldFetchLoom = shouldFetchV2Data && hasLoomEnabled && !shouldSkipDueToError;

	// Initialize Loom SDK if needed
	useLoomVideoConfig({
		cloudId: crossAppCreationConfig.enabled ? crossAppCreationConfig.cloudId : '',
		productName: convertSourceProductToProductType(product),
		environment: (crossAppCreationConfig.enabled && crossAppCreationConfig?.environment) || 'prod',
		onItemClick: crossAppCreationConfig.enabled ? crossAppCreationConfig.onItemClick : undefined,
		skipInitialisation: !shouldFetchLoom,
	});

	// Trigger fetch when conditions are met
	useEffect(
		function fetchUCMenuItemsForV2() {
			if (shouldFetchV2Data) {
				fetchUCMenuItems();
			}
		},
		[shouldFetchV2Data, fetchUCMenuItems],
	);

	// Check if we need to fetch user data (only recalculate when data changes)
	const hasUserItemsRequired = useMemo(() => {
		if (!data || shouldSkipDueToError || !data.sections || !Array.isArray(data.sections)) {
			return false;
		}

		// Check all sections for items requiring user data
		return data.sections.some(
			(section) =>
				section &&
				section.items &&
				Array.isArray(section.items) &&
				section.items.some((item) => item && item.ati && REQUIRED_USER_ITEMS.includes(item.ati)),
		);
	}, [data, shouldSkipDueToError]);

	const shouldFetchUser = shouldFetchV2Data && hasUserItemsRequired && !shouldSkipDueToError;

	return {
		shouldFetchV2Data: shouldFetchV2Data && !shouldSkipDueToError,
		shouldFetchLoom,
		shouldFetchUser,
	};
}
